import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getRem, FCX } from "@datassential/platform-ui-lib";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import cn from "classnames";

export type CarouselModifier = "fluid";

export type CarouselSlide = {
	id: string | number;
	content: React.ReactNode;
}

const SLIDES_IN_VIEWPORT = 5;

export type CarouselProps = FCX<{
	slides: CarouselSlide[];
	modifiers?: CarouselModifier[];
	gapPx?: number;
	minTileWidthPx?: number;
	previousArrow?: React.ReactNode;
	nextArrow?: React.ReactNode;
}>;

const Carousel: CarouselProps = ({
	slides,
	className,
	modifiers = [],
	gapPx = 20,
	minTileWidthPx = 240,
	previousArrow,
	nextArrow,
	testId,
	style,
	...props
}) => {
	const gap = getRem(gapPx);
	const rootRef = useRef<HTMLDivElement | null>(null);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const [containerWidth, setContainerWidth] = useState<number | null>(null);
	const [currentIndex, setCurrentIndex] = useState(0);
	const [isTransitioning, setIsTransitioning] = useState(false);
	const [disableTransition, setDisableTransition] = useState(false);

	useEffect(() => {
		if (!rootRef.current) return;
		
		const handleResize = () => {
			if (rootRef.current) {
				setContainerWidth(rootRef.current.offsetWidth);
			}
		};

		// Initial measurement
		handleResize();
		const observer = new window.ResizeObserver(handleResize);
		observer.observe(rootRef.current);
		return () => observer.disconnect();
	}, []);

	// Calculate visible slides based on container width
	const slidesInView = useMemo(() => {
		if (!containerWidth) return SLIDES_IN_VIEWPORT;
		const tileWidth = (containerWidth - gapPx * (SLIDES_IN_VIEWPORT - 1)) / SLIDES_IN_VIEWPORT;
		return tileWidth < minTileWidthPx
			? Math.floor((containerWidth + gapPx) / (minTileWidthPx + gapPx))
			: SLIDES_IN_VIEWPORT;
	}, [containerWidth, gapPx, minTileWidthPx]);

	// For seamless infinite, we need to clone slides
	const carouselSlides = useMemo(() => {
		if (slides.length === 0) return [];
		const prepend = slides.slice(-slidesInView);
		const append = slides.slice(0, slidesInView);
		return [...prepend, ...slides, ...append];
	}, [slides, slidesInView]);

	// Reset index on data change
	useEffect(() => {
		setCurrentIndex(slidesInView);
	}, [slidesInView]);	

	// Handle seamless jump after transition ends
	useEffect(() => {
		if (!isTransitioning) return;
		const ref = containerRef.current;
		if (!ref) return;

		const handle = () => {
			const realDataLength = slides.length;
			let newIndex = currentIndex;
			let didJump = false;

			if (currentIndex >= realDataLength + slidesInView) {
				newIndex = slidesInView;
				didJump = true;
			} else if (currentIndex < slidesInView) {
				newIndex = realDataLength + slidesInView - 1;
				didJump = true;
			}

			if (didJump) {
				setDisableTransition(true);
				setCurrentIndex(newIndex);
				requestAnimationFrame(() => {
					setDisableTransition(false);
					setIsTransitioning(false);
				});
			} else {
				setIsTransitioning(false);
			}
		};

		ref.addEventListener('transitionend', handle, { once: true });
		return () => ref.removeEventListener('transitionend', handle);
	}, [slides.length, currentIndex, isTransitioning, slidesInView]);

	const handlePrev = useCallback(() => {
		if (isTransitioning) return;
		setIsTransitioning(true);
		setCurrentIndex(prev => prev - 1);
	}, [isTransitioning]);

	const handleNext = useCallback(() => {
		if (isTransitioning) return;
		setIsTransitioning(true);
		setCurrentIndex(prev => prev + 1);
	}, [isTransitioning]);

	const slideWidth = useMemo(() => {
		const gapCount = slidesInView - 1;
		if (gapCount === 0) {
			return `calc(100% / ${slidesInView})`;
		}
		return `calc((100% - ${gapCount * gapPx}px) / ${slidesInView})`;
	}, [slidesInView, gapPx]);

	const translateX = useMemo(() => {
		return `calc(-${currentIndex} * (${slideWidth} + ${gapPx}px))`;
	}, [currentIndex, slideWidth, gapPx]);

	const carouselContainerStyle = useMemo(() => ({
		transform: `translateX(${translateX})`,
		transition: disableTransition || !isTransitioning
			? 'none'
			: 'transform 0.5s cubic-bezier(0.25, 0.6, 0.35, 1)',
		gap,
	}), [translateX, disableTransition, isTransitioning, gap]);

	const slideNodes = useMemo(() => {
		return carouselSlides.map((slide, idx) => (
			<div
				className={cn(
					"Carousel__slide",
					"appears-top",
					`del-${(idx % slidesInView) + 1}`,
					"fast"
				)}
				key={`${slide.id}${idx}`}
				style={{
					flex: `0 0 ${slideWidth}`,
					maxWidth: slideWidth,
				}}
			>
				{slide.content}
			</div>
		));
	}, [carouselSlides, slideWidth, slidesInView]);

	return (
		<div
			ref={rootRef}
			className={cn(
				"Carousel",
				className,
				...modifiers.map((m) => `is-${m}`)
			)}
			data-testid={testId}
			style={style}
			{...props}
		>
			<button
				className="Carousel__arrow left"
				onClick={handlePrev}
				aria-label="Previous"
				data-testid="buttonCarouselArrowLeft"
			>
				{previousArrow ?? <FiChevronLeft />}
			</button>
			<div className="Carousel__viewport">
				<div
					className="Carousel__container"
					ref={containerRef}
					style={carouselContainerStyle}
				>
					{slideNodes}
				</div>
			</div>
			<button
				className="Carousel__arrow right"
				onClick={handleNext}
				aria-label="Next"
				data-testid="buttonCarouselArrowRight"
			>
				{nextArrow ?? <FiChevronRight />}
			</button>
		</div>
	);
};

export default Carousel;